/**
 * Morse — „Augen zu“: hören, sagen, Rückmeldung. (13.10.2026, Stufe 1 „Geführt“, Reiter Hören)
 *
 * Martin: üben mit geschlossenen Augen. Ein Buchstabe klingt, das Mikrofon öffnet gleich danach, er SAGT den Buchstaben, eine
 * Rückmeldung kommt, dann der nächste. Kein Antippen, kein Feld, kein Prüffenster.
 *
 * Ablauf je Aufgabe (`spieleAufgabe` → `zuhoeren` → `auswerten` → `rueckmeldung` → `naechste`):
 *   1. Ton (u.spiele) — ein einzelner Buchstabe aus den gelernten Zeichen, gewichtet wie `kochAufgabe` (morse-augen-logik.ts).
 *   2. Am Ende des Tons, im selben Zug: der Kontext darf schlafen, die Mikro-Leitung wird angestoßen (`Zuhoerer`, über den
 *      vorhandenen DiktatAnschluss; der ruft `sitzungZurueck()` vor dem Mikro).
 *   3. Gesprochenes gegen die GESCHLOSSENE Menge der gelernten Zeichen (`waehleKandidat`); mehrdeutig oder still → noch einmal
 *      spielen, ohne Strafe (`urteile`).
 *   4. Richtig: heller Ton. Falsch: tiefer Ton, kurze Stimme nennt den Buchstaben, der Ton klingt noch einmal langsam.
 *   5. Erst danach die nächste Aufgabe — nach einem Fehler NICHT derselbe Buchstabe (23.10.2026, `nichtGleichNachFehler`).
 *
 * Gebucht wird wie beim Hören: je fünf Aufgaben EINE Koch-Aufgabe (Art `sprechen`, zählt für Güte und Aufstieg). Aufgaben ohne
 * Antwort (Stille) hängen „in der Schwebe“, bis eine beantwortete folgt — wer eingeschlafen ist, bucht keine Fehler. Nach
 * `AUGEN_OHNE_ANTWORT_PAUSE` Aufgaben in Folge ohne Antwort pausiert die Runde.
 *
 * ⚠️ Die Tonsitzung wird NICHT gehalten: `lassWach()` vor dem Mikrofon (der Kontext schläft), `wacheWieder()` erst für die
 *    Rückmeldung. Pause, Stopp, Verdecken: alles aus (Token), Mikro zu, Stimme still.
 * ⚠️ Die Mikro-Leitung entsteht hier OHNE Fingertipp (nur der erste „Start“ ist eine Geste). Ob iOS die Mitschrift dann
 *    trägt, prüft nur das Gerät — darum die Messzeile `[morse-sprechen]` (mikro-start-ms, erkannt, kandidat, sicher).
 * ⚠️ Keine Inline-Styles: alles in style.css (`.morse-augen…`). Kein Audio wird gespeichert, im Journal nur Zahlen.
 */
import { kochAuswertung, kochNeueZeichen, kochNeuesZeichen, kochUebungsZeichen, KOCH_REIHENFOLGE, type Umfang } from "../../shared/morse-koch.js";
import { MORSE_TABELLE } from "../../shared/morse.js";
import { musterBild, musterSprechen } from "./morse-bild.js";
import { baueKacheln } from "./morse-kacheln.js";
import { merkwortAria, merkwortElement } from "./morse-merkwort.js";
import { liesBuchstabe, merkeBuchstabe, type BuchstabeZeigen } from "./morse-hilfe.js";
import { Zuhoerer, type ZuhoerErgebnis } from "./morse-augen-hoeren.js";
import { AUGEN_KANDIDATEN_HOECHSTENS, darfSpicken, journalWort, kannNochWachsen, neuesBuendel, nichtGleichNachFehler, pausiertOhneAntwort, urteile, waehleKandidat, wirdGebucht, zeigtBuchstabe, type Urteil, type Wahl } from "./morse-augen-logik.js";
import { MorseStufe } from "./morse-stufe.js";
import { MorseUmfang } from "./morse-umfang.js";
import { AUGEN, TEMPO } from "./morse-texte.js";
import { Ablauf } from "./morse-ablauf.js";
import { rundenVerlauf } from "./morse-balken.js";
import { hoerZeiten, rundeAlsAufgaben, rundeErgebnis, unsichereZeichen, VORLAUF_MS, zuBuchen, type Antwort, type HoerRunde } from "./morse-runde.js";
import type { DiktatFehlerArt } from "./morse-sprechen.js";
import type { SprachWort } from "./morse-sprechen-leser.js";
import {
  baueBuehne,
  knopf,
  wahl,
  dauerText,
  el,
  fokusAufStart,
  insBild,
  keinTonText,
  prozent,
  zeichenKnopf,
  type Buehne,
  type BuehnenZustand,
  type MorseUebung,
  type MorseUmgebung,
  type StimmeLauf,
} from "./morse-teile.js";

/** Der Name des Reiters in den Diagnosezeilen — EINMAL, ASCII (greppbar: „[morse] … reiter=augen …“). */
const REITER = "augen";
/** Luft vor einer Wiederholung (ohne Strafe) und nach der Rückmeldung, bevor die nächste Aufgabe klingt. */
const NOCHMAL_LUECKE_MS = 600;
const NACH_RICHTIG_MS = 450;
/** Stille nach dem Tick, bevor der neue Buchstabe klingt. */
const NACH_TICK_MS = 250;

type Phase = "hoert" | "spricht" | "rueckmeldung";
/** Die Tippfelder einer Aufgabe. `letzt`: der letzte Versuch war unsicher — das Mikrofon ist zu, die Felder warten noch kurz. */
interface OffeneWahl {
  i: number;
  soll: string;
  nochmal: number;
  wdh: boolean;
  kandidaten: string[];
  letzt?: true;
}
/** So lange stehen die Felder nach dem letzten unsicheren Versuch, bevor aufgelöst wird („Nicht verstanden. Das war …“). */
const FELDER_LETZT_MS = 6000;

export class MorseAugenZu implements MorseUebung {
  readonly wurzel: HTMLElement;
  private readonly b: Buehne;
  private readonly ablauf: Ablauf;
  private readonly zuhoerer: Zuhoerer;
  private readonly bereitTeil: HTMLElement;
  private readonly kachelPlatz: HTMLElement;
  private readonly zeichenAnzeige: HTMLElement;
  private readonly zeigenKnopf: HTMLButtonElement;
  /** Die Tippfelder bei Unsicherheit (die 1–4 wahrscheinlichsten Buchstaben) — Tippen zählt wie Sagen. */
  private readonly kandidatTeil: HTMLElement;
  private readonly kandidatReihe: HTMLElement;
  private readonly kandidatTitel: HTMLElement;
  private readonly anzeigeBlock: HTMLElement;
  private readonly anzeigeWahl: ReturnType<typeof wahl<BuchstabeZeigen>>;
  private readonly anzeigeText: HTMLElement;
  private readonly umfangWahl: MorseUmfang;
  private readonly laufTeil: HTMLElement;
  private readonly buehnenBild: HTMLElement;
  private readonly phaseZeile: HTMLElement;
  private readonly fertigTeil: HTMLElement;
  private readonly stufeZeile: MorseStufe | null;

  private zustand: BuehnenZustand = "bereit";
  private phaseWert: Phase | null = null;
  private umfang: Umfang = "meine";
  private runde: HoerRunde = { art: "gruppen", gruppen: [], buendel: [] };
  private antworten: Array<Antwort | undefined> = [];
  /** Wo es nach der Rückmeldung weitergeht: die nächste Aufgabe — oder dieselbe Stelle, wenn die Aufgabe ohne Antwort entfernt wurde. */
  private nextIdx = 0;
  /** Der Buchstabe der Aufgabe, die gerade dran ist (oder zuletzt war) — nicht `runde.gruppen[index]`: eine entfernte Aufgabe verschöbe es. */
  private aktuellesZeichen = "";
  /** „Zeigen“ wurde in dieser Aufgabe getippt: sie zählt nicht (wie unbeantwortet, aber ohne die Pause-Zählung). */
  private gespickt = false;
  private anzeigeModus: BuchstabeZeigen = "nach";
  /** Läuft gerade die Wiederholung nach einem Fehler (derselbe Buchstabe, zählt nicht, bucht nichts)? */
  private wiederholung = false;
  private gebucht = new Set<number>();
  private index = 0;
  private weiterBei = 0;
  private ohneAntwort = 0;
  private rundeNr = 0;
  private wachGeloest = false;
  private stimmeLauf: StimmeLauf | null = null;
  /** Die Aufgabe, zu der die Tippfelder gerade stehen — `null`: keine Felder. */
  private offeneWahl: OffeneWahl | null = null;
  private tempoBeimStart = "";

  constructor(
    private readonly u: MorseUmgebung,
    tafel: HTMLElement,
    /** Der Zustand hat sich geändert (läuft/ruht/aus) — der Hören-Reiter sperrt dann die Wahl des Modus. */
    private readonly beiZustand: () => void = () => undefined,
  ) {
    this.b = baueBuehne(
      "Übung Augen zu",
      (rolle) => (rolle === "start" ? this.start() : this.stopp("knopf")),
      (rolle) => (rolle === "pause" ? this.pause("knopf") : this.weiter()),
      u.zeigeTabelle ? () => u.zeigeTabelle?.() : undefined,
    );
    this.wurzel = this.b.wurzel;
    this.wurzel.classList.add("morse-augen");
    tafel.appendChild(this.wurzel);
    this.ablauf = new Ablauf(u, "sprechen", this.b);
    this.zuhoerer = new Zuhoerer(u.sprechen!, () => u.jetzt());
    const f = this.b.flaeche;

    this.fertigTeil = el("div", "morse-fertig");

    // Läuft: ein großes, ruhiges Bild der Phase und ein Satz dazu — wer hinsieht, sieht es; wer die Augen zu hat, braucht es nicht.
    this.laufTeil = el("div", "morse-lauf morse-augen-lauf");
    this.buehnenBild = el("div", "morse-augen-bild");
    this.buehnenBild.setAttribute("aria-hidden", "true");
    this.phaseZeile = el("p", "morse-phase morse-augen-phase");
    this.phaseZeile.setAttribute("aria-live", "polite");
    // Der Buchstabe (groß, mit Morse-Bild) — verdeckt („?“), bis er gezeigt werden darf — und „Zeigen“ zum Spicken.
    this.zeichenAnzeige = el("div", "morse-augen-zeichen");
    this.zeigenKnopf = knopf(AUGEN.anzeige.zeigen, "morse-augen-zeigen", () => this.spicke());
    this.zeigenKnopf.setAttribute("aria-label", AUGEN.anzeige.zeigenAria);
    // Nicht sicher verstanden: die wahrscheinlichsten Buchstaben groß zum Antippen („zur Not drauf drücken“, 21.10.2026).
    this.kandidatTeil = el("div", "morse-augen-kandidaten");
    this.kandidatTeil.hidden = true;
    this.kandidatReihe = el("div", "morse-augen-kandidaten-reihe");
    this.kandidatReihe.setAttribute("role", "group");
    this.kandidatReihe.setAttribute("aria-label", AUGEN.kandidaten.gruppe);
    this.kandidatTitel = el("p", "morse-augen-kandidaten-titel", AUGEN.kandidaten.titel);
    this.kandidatTeil.append(this.kandidatTitel, this.kandidatReihe);
    this.laufTeil.append(this.buehnenBild, this.phaseZeile, this.zeichenAnzeige, this.kandidatTeil, this.zeigenKnopf);

    // Bereit: was es ist, womit geübt wird, welche Zeichen dran sind.
    this.bereitTeil = el("div", "morse-bereit");
    this.kachelPlatz = el("div", "morse-dran-platz");
    this.umfangWahl = new MorseUmfang(u, () => this.zeichneBereit());
    this.bereitTeil.append(el("p", "morse-anleitung", AUGEN.erklaerung), this.umfangWahl.wurzel, this.kachelPlatz);
    // Der Dreifach-Schalter steht immer da (auch in der Runde): wer die Augen aufmacht, kann ihn gleich umstellen.
    this.anzeigeModus = liesBuchstabe();
    this.anzeigeBlock = el("div", "morse-augen-anzeige");
    this.anzeigeWahl = wahl<BuchstabeZeigen>(
      AUGEN.anzeige.titel,
      [
        { id: "aus", text: AUGEN.anzeige.aus },
        { id: "nach", text: AUGEN.anzeige.nach },
        { id: "immer", text: AUGEN.anzeige.immer },
      ],
      (id) => {
        this.anzeigeModus = id;
        merkeBuchstabe(id);
        this.zeichneAnzeige();
        // Die Merkwörter in „Gerade dran“ folgen demselben Schalter (bei „Aus“ keine Sehhilfe).
        if (this.zustand !== "laeuft" && this.zustand !== "pause") this.zeichneBereit();
      },
    );
    this.anzeigeWahl.wurzel.classList.add("morse-wahl--voll");
    this.anzeigeText = el("p", "morse-hinweis");
    this.anzeigeBlock.append(el("p", "morse-feld-titel", AUGEN.anzeige.titel), this.anzeigeWahl.wurzel, this.anzeigeText);

    this.stufeZeile = u.stufe ? new MorseStufe(u.stufe, { kompakt: true, beiWechsel: () => this.b.melde("") }) : null;
    if (this.stufeZeile) this.stufeZeile.wurzel.hidden = true;
    f.append(this.fertigTeil, ...(this.stufeZeile ? [this.stufeZeile.wurzel] : []), this.laufTeil, this.bereitTeil, this.anzeigeBlock);
    this.zeige("bereit", "");
    this.zeichneBereit();
  }

  /* ── Zustand von außen ─────────────────────────────────────────── */

  get laeuft(): boolean {
    return this.zustand === "laeuft";
  }
  get pausiert(): boolean {
    return this.zustand === "pause";
  }

  private get phase(): Phase | null {
    return this.phaseWert;
  }
  private set phase(p: Phase | null) {
    this.phaseWert = p;
    this.wurzel.dataset.phase = p ?? "aus";
    this.zeichneAnzeige();
  }

  /* ── Bedienung ──────────────────────────────────────────────────── */

  start(): void {
    if (this.zustand === "laeuft" || this.zustand === "pause") return;
    const s = this.u.stand();
    this.umfang = this.umfangWahl.umfang();
    this.runde = { art: "gruppen", gruppen: [], buendel: [] };
    this.fuelleBuendel();
    this.tempoBeimStart = `${s.einstellungen.tempo}/${s.einstellungen.wirksam}`;
    this.ablauf.neueRunde(this.umfang);
    this.stufeZeile?.zuruecksetzen();
    this.rundeNr++;
    this.gebucht = new Set();
    this.antworten = [];
    this.nextIdx = 0;
    this.wiederholung = false;
    // Eine neue Runde beginnt ohne den Buchstaben und das „gespickt“ der alten (der Vorlauf zeigte sonst den letzten Buchstaben).
    this.gespickt = false;
    this.aktuellesZeichen = "";
    this.index = 0;
    this.ohneAntwort = 0;
    this.wachGeloest = false;
    this.b.melde("");
    this.zeige("laeuft", AUGEN.zaehler(1, 0));
    this.phase = null;
    this.phaseText(AUGEN.phase.vorlauf);
    insBild(this.b.wurzel);
    // Noch in der Geste: den Ton wecken (und für die Runde wach halten, bis das Mikrofon kommt).
    this.ablauf.halteWach(`start reiter=${REITER} stufe=${s.stufe}${this.umfang === "alle" ? " umfang=alle" : ""}`);
    this.ablauf.zeitLaeuft();
    this.ablauf.spaeter(VORLAUF_MS, () => void this.spieleAufgabe(0, 0));
  }

  stopp(grund: string): void {
    if (this.zustand !== "laeuft" && this.zustand !== "pause") return;
    this.beende(grund);
  }

  pause(grund: string, gehtWeg = false): void {
    if (this.zustand !== "laeuft") return;
    this.allesAus();
    this.ablauf.zeitHalt();
    // Mitten in der Rückmeldung (oder gerade beantwortet) geht es mit der nächsten weiter, sonst mit dieser noch einmal.
    // In der Rückmeldung — und in der Wiederholung (die schon beantwortete Aufgabe wird nicht noch einmal gestellt) — geht es mit der nächsten weiter.
    // Eine gespickte Aufgabe (der Buchstabe stand in der Frage auf dem Bildschirm) wird nach der Pause NICHT neu gestellt und
    // nicht gebucht: sie fällt aus der Runde, „Weiter“ spielt den nächsten Buchstaben.
    const gespicktOffen = this.gespickt && (this.phase === "hoert" || this.phase === "spricht") && !this.wiederholung;
    if (gespicktOffen) {
      this.runde.gruppen.splice(this.index, 1);
      this.runde.buendel.splice(this.index, 1);
      this.antworten.splice(this.index, 1);
      this.nextIdx = this.index;
    }
    this.weiterBei = this.phase === "rueckmeldung" || this.wiederholung ? this.nextIdx : this.index;
    this.gespickt = false;
    this.wiederholung = false;
    this.phase = null;
    this.ablauf.lassWach();
    this.wachGeloest = false;
    this.u.spur(`pause reiter=${REITER} grund=${grund} aufgabe=${this.index + 1}`);
    void this.bucheRest(gehtWeg);
    this.zeige("pause");
    const naechste = this.weiterBei !== this.index || gespicktOffen;
    this.phaseText(grund === "stille" ? AUGEN.pause.stille : naechste ? AUGEN.pause.naechste : AUGEN.pause.knopf);
  }

  /** Pause mit einem Satz, warum (Mikrofon-Fehler, Sperre) — der Satz steht in der Statuszeile. */
  private pauseMit(grund: string, satz: string): void {
    this.pause(grund);
    this.b.melde(satz, "fehler");
  }

  weiter(): void {
    if (this.zustand !== "pause") return;
    this.zeige("laeuft");
    this.b.melde("");
    this.phaseText(AUGEN.phase.vorlauf);
    this.wachGeloest = false;
    this.ohneAntwort = 0;
    this.ablauf.halteWach(`weiter reiter=${REITER}`);
    this.ablauf.zeitLaeuft();
    const bei = this.weiterBei;
    this.ablauf.spaeter(VORLAUF_MS, () => void this.spieleAufgabe(bei, 0));
  }

  leertaste(art: "keydown" | "keyup", wiederholt: boolean): boolean {
    if (art === "keyup") return this.zustand !== "bereit";
    if (wiederholt) return true;
    if (this.zustand === "laeuft") this.stopp("leertaste");
    else if (this.zustand === "pause") this.weiter();
    else this.start();
    return true;
  }

  ruhe(grund: string, gehtWeg = false): void {
    this.pause(grund, gehtWeg);
  }

  standGeaendert(): void {
    if (this.zustand !== "laeuft" && this.zustand !== "pause") this.zeichneBereit();
  }

  zerstoere(): void {
    this.allesAus();
    this.ablauf.lassWach();
  }

  /** Zeichnet den Bereit-Teil neu (der Hören-Reiter ruft es beim Wechsel des Modus). */
  zeichneBereit(): void {
    const s = this.u.stand();
    this.umfangWahl.zeichne();
    const alle = this.umfangWahl.umfang() === "alle";
    // Das Merkwort steht in den Kacheln, solange der Buchstabe überhaupt gezeigt werden darf (Schalter „Buchstabe zeigen“ nicht „Aus“).
    this.kachelPlatz.replaceChildren(
      baueKacheln(kochUebungsZeichen(s.stufe, this.umfangWahl.umfang()), alle ? [] : kochNeueZeichen(s.stufe), { merkwort: this.anzeigeModus !== "aus" }),
    );
    this.stufeZeile?.zeichne(s);
  }

  /* ── Ablauf ─────────────────────────────────────────────────────── */

  /** Das nächste Bündel (fünf Buchstaben) anhängen — nach der Stufe von JETZT (ein Aufstieg in der Runde zählt sofort). */
  private fuelleBuendel(): void {
    const s = this.u.stand();
    const guete: Record<string, number> = {};
    for (const z of s.zeichen) guete[z.zeichen] = z.guete;
    const nr = this.runde.buendel.length ? this.runde.buendel[this.runde.buendel.length - 1] + 1 : 0;
    for (const z of neuesBuendel(s.stufe, { guete, zufall: () => this.u.zufall(), umfang: this.umfang })) {
      this.runde.gruppen.push(z);
      this.runde.buendel.push(nr);
    }
  }

  private async spieleAufgabe(i: number, nochmal: number, wdh = false): Promise<void> {
    if (this.zustand !== "laeuft") return;
    while (i >= this.runde.gruppen.length) this.fuelleBuendel();
    const soll = this.runde.gruppen[i];
    const tok = ++this.ablauf.token;
    this.index = i;
    this.aktuellesZeichen = soll;
    if (nochmal === 0) {
      this.gespickt = false;
      this.zeigeKandidaten(null);
    }
    this.wiederholung = wdh;
    this.wacheWieder();
    this.phase = "hoert";
    this.b.zaehler(AUGEN.zaehler(i + 1, this.richtige()));
    this.zeichneVerlauf();
    this.phaseText(wdh ? AUGEN.phase.nochmalGleich : nochmal > 0 ? AUGEN.phase.nochmal : AUGEN.phase.hoert);
    const e = await this.u.spiele(soll);
    if (tok !== this.ablauf.token) return;
    if (e === "kein-ton") return this.keinTon();
    if (e !== "fertig") return;
    this.zuhoeren(i, soll, nochmal, tok, wdh);
  }

  /** Der Ton ist zu Ende: das Mikrofon JETZT, im selben Zug. */
  private zuhoeren(i: number, soll: string, nochmal: number, tok: number, wdh: boolean): void {
    const sperre = this.u.sprechen!.sperre();
    if (sperre) {
      this.pauseMit("sperre", AUGEN.pause.sperre(sperre));
      return;
    }
    // Der Ton darf schlafen, bevor das Mikrofon kommt (Vorfall: eine gehaltene Wiedergabe-Sitzung nimmt dem Mikro die Aufnahme).
    this.ablauf.lassWach();
    this.wachGeloest = true;
    this.phase = "spricht";
    this.phaseText(AUGEN.phase.spricht);
    // Steht schon ein klarer Buchstabe der Menge im Gehörten, endet die Aufnahme gleich (keine lange Stille für eine Silbe).
    const menge = kochUebungsZeichen(this.u.stand().stufe, this.umfang);
    const frueh = (text: string, woerter?: readonly SprachWort[]): boolean => waehleKandidat(woerter?.length ? woerter : text, menge, { soll }).art === "treffer";
    // Ohne Abschluss nur, was nicht mehr zu einem anderen Buchstaben wachsen kann („Em“ → „Emil“, Prüfer-Auflage 23.10.).
    const sofort = (text: string, woerter?: readonly SprachWort[]): boolean => !kannNochWachsen(woerter?.length ? woerter : text, menge);
    void this.zuhoerer.hoere({ frueh, sofort }).then((r) => {
      if (tok !== this.ablauf.token) return;
      this.auswerten(i, soll, nochmal, r, tok, wdh);
    });
  }

  private auswerten(i: number, soll: string, nochmal: number, r: ZuhoerErgebnis, tok: number, wdh: boolean): void {
    if (r.grund === "abbruch") return;
    if (r.grund === "fehler") {
      this.messe(r, null, null, soll, nochmal, wdh);
      this.pauseMit("mikro", this.fehlerSatz(r.fehler ?? "sonst"));
      return;
    }
    this.phaseText(AUGEN.phase.wertet);
    const menge = kochUebungsZeichen(this.u.stand().stufe, this.umfang);
    const w = waehleKandidat(r.woerter?.length ? r.woerter : r.text, menge, { lang: r.grund === "hoechstens", soll, vorsichtig: r.nachgeholt === true });
    const urteil = urteile(soll, w, nochmal);
    this.messe(r, w, urteil, soll, nochmal, wdh);
    if (urteil.art === "nochmal") {
      this.phase = "hoert";
      // Nicht sicher verstanden: die Vorschläge stehen als Tippfelder, während der Buchstabe noch einmal klingt und zuhört.
      const neu = this.vorschlaege(i, soll, w);
      this.zeigeKandidaten(neu.length ? { i, soll, nochmal: nochmal + 1, wdh, kandidaten: neu } : null);
      this.phaseText(wdh ? AUGEN.phase.nochmalGleich : AUGEN.phase.nochmal);
      this.ablauf.spaeter(NOCHMAL_LUECKE_MS, () => void this.spieleAufgabe(i, nochmal + 1, wdh));
      return;
    }
    // Der letzte Versuch war auch unsicher: gibt es Vorschläge, bleiben die Felder noch `FELDER_LETZT_MS` stehen (Mikrofon zu) —
    // sie sind die Hilfe, wenn die Stimme nicht trifft. Erst danach wird aufgelöst (nicht gebucht, wie bisher).
    if (urteil.art === "unklar") {
      const neu = this.vorschlaege(i, soll, w);
      if (neu.length) {
        this.phase = "hoert";
        this.zeigeKandidaten({ i, soll, nochmal, wdh, kandidaten: neu, letzt: true });
        this.phaseText(AUGEN.phase.tippAn);
        this.ablauf.spaeter(FELDER_LETZT_MS, () => this.entscheide(i, soll, urteil, tok, wdh));
        return;
      }
    }
    this.entscheide(i, soll, urteil, tok, wdh);
  }

  /** Die Vorschläge dieser Wahl, ergänzt um die schon stehenden derselben Aufgabe (höchstens `AUGEN_KANDIDATEN_HOECHSTENS`). */
  private vorschlaege(i: number, soll: string, w: Wahl): string[] {
    const bisher = this.offeneWahl?.i === i && this.offeneWahl.soll === soll ? this.offeneWahl.kandidaten : [];
    return [...(w.kandidaten ?? []), ...bisher.filter((z) => !w.kandidaten?.includes(z))].slice(0, AUGEN_KANDIDATEN_HOECHSTENS);
  }

  /** Ein Tippfeld: der Buchstabe zählt wie gesagt — gleiches Urteil, gleiche Buchung, gleiche Rückmeldung. */
  private tippe(z: string): void {
    const o = this.offeneWahl;
    if (!o || this.zustand !== "laeuft" || (this.phase !== "hoert" && this.phase !== "spricht")) return;
    // Ton, Mikrofon, Uhren: alles aus — die Aufnahme dieser Aufgabe gilt nicht mehr.
    this.allesAus();
    const tok = this.ablauf.token;
    const w: Wahl = { art: "treffer", kandidat: z, sicher: 100, woerter: 0 };
    const urteil = urteile(o.soll, w, o.nochmal);
    this.messe(null, w, urteil, o.soll, o.nochmal, o.wdh, o.kandidaten.length);
    this.entscheide(o.i, o.soll, urteil, tok, o.wdh);
  }

  /** Nach dem Urteil (gesagt oder getippt): merken oder aus der Runde nehmen, dann die Rückmeldung. */
  private entscheide(i: number, soll: string, urteil: Urteil, tok: number, wdh: boolean): void {
    this.zeigeKandidaten(null);
    // Die Wiederholung nach einem Fehler zählt NICHT: nur der erste Versuch steht in der Buchung. Lärm darin zählt für die Pause.
    if (wdh) {
      this.ohneAntwort = urteil.beantwortet ? 0 : this.ohneAntwort + 1;
    } else if (this.gespickt) {
      // Gespickt: die Aufgabe zählt nicht (kein Treffer, keine Güte, keine Stufe) — aber er ist da, also keine Pause-Zählung.
      this.runde.gruppen.splice(i, 1);
      this.runde.buendel.splice(i, 1);
      this.antworten.splice(i, 1);
      this.nextIdx = i;
      if (urteil.beantwortet) this.ohneAntwort = 0;
    } else {
      this.speichere(i, soll, urteil);
    }
    void this.rueckmeldung(i, soll, urteil, tok, wdh);
  }

  /** Die Tippfelder zeigen (oder mit `null` wegnehmen). Große Felder, nur die wahrscheinlichsten Buchstaben. */
  private zeigeKandidaten(o: OffeneWahl | null): void {
    this.offeneWahl = o;
    this.kandidatTeil.hidden = !o;
    this.kandidatTitel.textContent = o?.letzt ? AUGEN.kandidaten.titelLetzt : AUGEN.kandidaten.titel;
    this.kandidatReihe.textContent = "";
    for (const z of o?.kandidaten ?? []) {
      const b = knopf(z, "morse-augen-kandidat", () => this.tippe(z));
      b.setAttribute("aria-label", AUGEN.kandidaten.aria(z));
      this.kandidatReihe.appendChild(b);
    }
  }

  /**
   * Eine fertige Aufgabe merken. NUR ein klar erkannter Buchstabe (richtig oder ein anderer) zählt und wird gebucht. Ohne Treffer (Stille, „äh“, Fernseher, fremde Sprache) ändert sie nichts an Güte
   * und Stufe: sie wird aus der Runde genommen, die nächste Aufgabe rückt an ihre Stelle, und der Zähler „ohne Treffer in Folge“
   * wächst (Grenze `AUGEN_OHNE_ANTWORT_PAUSE` → Pause, das Mikrofon öffnet nicht endlos neu).
   */
  private speichere(i: number, soll: string, u: Urteil): void {
    if (wirdGebucht(u)) {
      this.antworten[i] = { soll, ist: u.ist, auswertung: kochAuswertung(soll, u.ist) };
      this.ohneAntwort = 0;
      this.nextIdx = i + 1;
      return;
    }
    // Unklar (nichts Zuordenbares, oder mehrdeutig bis zuletzt): nicht gebucht. Mehrdeutig war eine Antwort — keine Pause-Zählung.
    this.runde.gruppen.splice(i, 1);
    this.runde.buendel.splice(i, 1);
    this.antworten.splice(i, 1);
    this.ohneAntwort = u.beantwortet ? 0 : this.ohneAntwort + 1;
    this.nextIdx = i;
  }

  private async rueckmeldung(i: number, soll: string, u: Urteil, tok: number, wdh: boolean): Promise<void> {
    this.phase = "rueckmeldung";
    this.wacheWieder();
    const richtig = u.art === "richtig";
    const unklar = u.art === "unklar";
    this.phaseText(richtig ? (u.merkwort ? AUGEN.phase.richtigMerkwort(soll, u.merkwort) : AUGEN.phase.richtig) : unklar ? AUGEN.phase.unklar(soll) : AUGEN.phase.falsch(soll));
    this.wurzel.dataset.urteil = richtig ? "richtig" : unklar ? "unklar" : "falsch";
    this.b.zaehler(AUGEN.zaehler(i + 1, this.richtige()));
    this.zeichneVerlauf();
    if (richtig) {
      await this.u.quittung?.("richtig");
    } else if (unklar) {
      // Nicht verstanden ist nicht falsch: kein tiefer Ton — die Stimme sagt es ehrlich und nennt den Buchstaben.
      await this.sprich(AUGEN.stimmeUnklar(soll));
      if (tok !== this.ablauf.token) return;
      if (!wdh) await this.u.spiele(soll, true);
    } else {
      await this.u.quittung?.("falsch");
      if (tok !== this.ablauf.token) return;
      await this.sprich(AUGEN.stimme(soll));
      if (tok !== this.ablauf.token) return;
      // Der erste Versuch: der Buchstabe noch einmal langsam. Die Wiederholung: nur die kurze Auflösung, dann weiter.
      if (!wdh) await this.u.spiele(soll, true);
    }
    if (tok !== this.ablauf.token) return;
    this.bucheVolle();
    // 23.10.2026 (Martin: „muss nicht sofort wiederholt werden“): nach einem Fehler KEINE Wiederholung derselben Aufgabe mehr
    // (bis dahin seit 13.10. einmal je Fehler) — und die nächste Aufgabe ist nicht gleich wieder dieser Buchstabe. `wdh` bleibt
    // für die Messzeile (`wiederholung=0`) und für Pause/Stopp-Pfade stehen, wird aber nicht mehr gesetzt.
    if (u.art === "falsch") {
      while (this.nextIdx >= this.runde.gruppen.length) this.fuelleBuendel();
      nichtGleichNachFehler(this.runde.gruppen, this.runde.buendel, this.nextIdx, soll);
    }
    const luecke = richtig ? NACH_RICHTIG_MS : hoerZeiten(this.u.stand().stufe).lueckeMs;
    // Nach „richtig“ war der helle Piep eben erst — er trennt schon; der Tick dazu machte zwei Töne (15.10., Martin: „nur EINEN Ton“).
    this.ablauf.spaeter(luecke, () => void this.naechste(!richtig));
  }

  private async sprich(text: string): Promise<void> {
    const lauf = this.u.stimme?.(text);
    if (!lauf) return;
    this.stimmeLauf = lauf;
    try {
      await lauf.fertig;
    } catch {
      /* Die Stimme ist Beiwerk: ohne sie bleiben der tiefe Ton und der langsame Buchstabe. */
    }
    if (this.stimmeLauf === lauf) this.stimmeLauf = null;
  }

  /**
   * Die nächste (neue) Aufgabe. `mitTick`: ein kurzer, leiser Tick kündigt sie an — aber nur, wenn zuletzt KEIN Quittungston
   * kam. Nach „richtig“ ist der helle Piep das Zeichen für „weiter“ (EIN Ton statt Piep + Tick, 15.10.2026, Martin); nach
   * „falsch“/„nicht verstanden“ klang zuletzt die Stimme oder der langsame Buchstabe — dort trennt der Tick den neuen ab.
   */
  private async naechste(mitTick: boolean): Promise<void> {
    if (this.zustand !== "laeuft") return;
    this.allesAus();
    this.wiederholung = false;
    if (pausiertOhneAntwort(this.ohneAntwort)) {
      this.pause("stille");
      return;
    }
    // Ein NEUER Buchstabe kündigt sich mit einem kurzen, leisen Tick an — vor der Wiederholung nach einem Fehler und nach
    // dem hellen Piep („richtig“) nicht.
    const tok = ++this.ablauf.token;
    this.phaseText(AUGEN.phase.neu);
    if (!mitTick) {
      void this.spieleAufgabe(this.nextIdx, 0);
      return;
    }
    await this.u.quittung?.("weiter");
    if (tok !== this.ablauf.token) return;
    this.ablauf.spaeter(NACH_TICK_MS, () => void this.spieleAufgabe(this.nextIdx, 0));
  }

  private beende(grund: string): void {
    const r = this.runde;
    this.allesAus();
    this.ablauf.zeitHalt();
    this.phase = null;
    this.ablauf.lassWach();
    this.wachGeloest = false;
    const aufgaben = rundeAlsAufgaben(r, this.antworten);
    const beantwortet = this.antworten.filter(Boolean) as Antwort[];
    this.u.spur(`stopp reiter=${REITER} grund=${grund} aufgaben=${beantwortet.length} ton=${this.u.spieler.zustand()}`);
    this.zeige("fertig", `${beantwortet.length} ${beantwortet.length === 1 ? "Aufgabe" : "Aufgaben"}`);
    fokusAufStart(this.b);
    this.zeichneFertig(aufgaben, beantwortet);
    this.zeichneBereit();
    if (!aufgaben.length) {
      this.b.melde(AUGEN.fertig.leer, "info");
      return;
    }
    const nr = this.rundeNr;
    this.b.melde("Speichere …", "info");
    void this.bucheRest(false).then(() => {
      if (nr !== this.rundeNr || this.zustand !== "fertig") return;
      this.zeichneBereit();
      if (this.ablauf.hatOffenes) {
        this.b.melde("");
        return;
      }
      const d = this.ablauf.aufstieg;
      if (!d) {
        this.b.melde("Gespeichert.", "info");
        return;
      }
      const neu = kochNeuesZeichen(d.stufe);
      const e = this.u.stand().einstellungen;
      const schneller = `${e.tempo}/${e.wirksam}` !== this.tempoBeimStart ? TEMPO.schneller(e.tempo, e.wirksam) : "";
      this.b.melde(`Stufe ${d.stufe}! Neu dazu: ${neu} (${musterSprechen(MORSE_TABELLE[neu])}). Lern es im Reiter „Tippen“ unter „Deine Zeichen“ kennen.${schneller}`, "gut");
      this.u.feiere?.(d);
      if (this.stufeZeile && this.u.stand().stufe >= 2) {
        this.stufeZeile.zuruecksetzen();
        this.stufeZeile.zeichne(this.u.stand());
        this.stufeZeile.wurzel.hidden = false;
      }
    });
  }

  private keinTon(): void {
    const zustand = this.u.spieler.zustand();
    this.u.spur(`kein-ton reiter=${REITER} zustand=${zustand} aufgabe=${this.index + 1}`);
    this.allesAus();
    this.ablauf.zeitHalt();
    this.phase = null;
    this.ablauf.lassWach();
    void this.bucheRest(false);
    this.zeige("fehler", "");
    fokusAufStart(this.b);
    this.b.melde(keinTonText(zustand), "fehler");
  }

  /* ── Buchen ─────────────────────────────────────────────────────── */

  /** Das volle Bündel (alle fünf beantwortet) geht sofort an den Server — verwirft iOS die Seite, ist es schon gespeichert. */
  private bucheVolle(): void {
    const z = zuBuchen(this.runde, this.antworten, this.gebucht, true);
    if (!z.aufgaben.length) return;
    for (const g of z.gruppen) this.gebucht.add(g);
    void this.ablauf.sende(this.ablauf.satz(z.aufgaben));
  }

  private async bucheRest(gehtWeg: boolean): Promise<void> {
    const z = zuBuchen(this.runde, this.antworten, this.gebucht);
    if (z.aufgaben.length) {
      for (const g of z.gruppen) this.gebucht.add(g);
      void this.ablauf.sende(this.ablauf.satz(z.aufgaben), gehtWeg);
    }
    await this.ablauf.warte();
  }

  /* ── Messung und Sätze ──────────────────────────────────────────── */

  /**
   * Die Messzeile `[morse-sprechen] mikro-start-ms=… erkannt=… kandidat=… sicher=…` — Zahlen und feste Stichwörter.
   * `erkannt`: Wörter der Mitschrift, `kandidat`: 1 = ein Buchstabe gewählt, `sicher`: 0…100, `soll`: Platz des geübten Zeichens in
   * der Koch-Reihenfolge (eine Zahl, nie der Buchstabe), `urteil`/`grund`: feste Wörter. Nie Audio. Das Gesagte NUR als
   * `wort=` bei kandidat=0 und höchstens 12 Buchstaben (`journalWort`: Üb-Kontext mit Einzelbuchstaben, ein Satz wird „…“).
   * `stille-ms` + `abschluss-ms` = vom letzten neuen Wort bis zum Urteil (die Auswertung selbst ist synchron).
   */
  private messe(r: ZuhoerErgebnis | null, w: Wahl | null, u: Urteil | null, soll: string, nochmal: number, wdh: boolean, getipptAus?: number): void {
    const getippt = r === null;
    const zeile = [
      `mikro-start-ms=${r?.mikroMs ?? -1}`,
      `erkannt=${w?.woerter ?? 0}`,
      `kandidat=${w?.kandidat ? 1 : 0}`,
      `sicher=${w?.sicher ?? 0}`,
      `urteil=${u?.art ?? "fehler"}`,
      `grund=${getippt ? "getippt" : r.grund}`,
      `nochmal=${nochmal}`,
      `wiederholung=${wdh ? 1 : 0}`,
      `gespickt=${this.gespickt ? 1 : 0}`,
      `soll=${KOCH_REIHENFOLGE.indexOf(soll)}`,
      // Latenz: letztes neues Wort → Mikro zu (Stille-Frist) und Mikro zu → Urteil (Abschluss der Erkennung); frueh=1: klarer Buchstabe.
      `stille-ms=${r?.stilleMs ?? -1}`,
      `abschluss-ms=${r?.abschlussMs ?? -1}`,
      `frueh=${r?.frueh ? 1 : 0}`,
    ];
    // Ohne gewählten Buchstaben: das Gehörte, kurz und nur Buchstaben (`journalWort`, höchstens 12) — so wird eine Schreibweise
    // sichtbar, die in keiner Tabelle steht. erwartet=1: der Treffer kam nur aus der Erwartung (M-Laut bei soll = M).
    if (r && w && !w.kandidat && w.woerter > 0) zeile.push(`wort=${journalWort(r.woerter?.length ? r.woerter : r.text)}`);
    if (w?.erwartet) zeile.push("erwartet=1");
    // Nur Zahlen: wie viele Tippfelder (nie welche Buchstaben), ob die Erkennung zurückzog, ob getippt wurde (aus wie vielen).
    if (w?.kandidaten?.length) zeile.push(`felder=${w.kandidaten.length}`);
    if (r?.nachgeholt) zeile.push("nachgeholt=1");
    if (getippt) zeile.push(`getippt=1 felder=${getipptAus ?? 0}`);
    const text = zeile.join(" ");
    try {
      (this.u.messe ?? ((t: string) => this.u.spur(`sprechen ${t}`)))(text);
    } catch {
      /* Diagnose ist nie wichtiger als die Übung */
    }
  }

  private fehlerSatz(art: DiktatFehlerArt): string {
    return art === "recht" ? AUGEN.fehler.recht : art === "kein-mikro" ? AUGEN.fehler.keinMikro : art === "netz" ? AUGEN.fehler.netz : AUGEN.fehler.sonst;
  }

  /* ── Anzeige und Uhr ────────────────────────────────────────────── */

  private richtige(): number {
    return this.antworten.filter((a) => a && a.auswertung.richtig === a.auswertung.gesamt && a.auswertung.zuviel === 0).length;
  }

  /** Der Verlauf (morse-balken.ts): Augen zu läuft ohne Ende — die letzten zwanzig Aufgaben, die jetzige umrandet. */
  private zeichneVerlauf(): void {
    if (this.zustand === "bereit" || this.zustand === "fehler") return this.b.verlauf(null);
    const ergebnisse = this.antworten.map((a) => (a ? a.auswertung.richtig === a.auswertung.gesamt && a.auswertung.zuviel === 0 : undefined));
    const jetzt = this.zustand === "fertig" || this.antworten[this.index] !== undefined ? null : this.index;
    this.b.verlauf(rundenVerlauf(ergebnisse, { jetzt, fenster: 20 }));
  }

  private zeige(z: BuehnenZustand, zaehler?: string): void {
    this.zustand = z;
    this.b.setze(z, { zaehler });
    this.bereitTeil.hidden = z === "laeuft" || z === "pause";
    if (z !== "fertig" && this.stufeZeile) this.stufeZeile.wurzel.hidden = true;
    this.laufTeil.hidden = !(z === "laeuft" || z === "pause");
    this.fertigTeil.hidden = z !== "fertig";
    if (z !== "laeuft") delete this.wurzel.dataset.urteil;
    this.zeichneAnzeige();
    this.zeichneVerlauf();
    this.beiZustand();
  }

  /**
   * Der Satz zur Phase (aria-live). Stehen Tippfelder, kommt die Frage dazu („Meintest du: K, M?“), damit ein Screenreader sie
   * ansagt — die Felder kommen nur aus dem Gehörten, die Frage verrät also nichts.
   */
  private phaseText(t: string): void {
    const voll = this.offeneWahl ? `${t} ${AUGEN.kandidaten.frage(this.offeneWahl.kandidaten)}` : t;
    if (this.phaseZeile.textContent !== voll) this.phaseZeile.textContent = voll;
  }

  private zeichneFertig(aufgaben: Array<{ soll: string; ist: string }>, beantwortet: Antwort[]): void {
    const t = this.fertigTeil;
    t.textContent = "";
    if (!aufgaben.length) {
      t.appendChild(el("p", "morse-ergebnis-text", "Diese Runde ist ohne Antwort zu Ende."));
      return;
    }
    const e = rundeErgebnis(aufgaben);
    const kopf = el("div", "morse-ergebnis");
    kopf.append(
      el("p", "morse-ergebnis-zahl", prozent(e.quote)),
      el("p", "morse-ergebnis-text", `${AUGEN.fertig.ergebnis(e.richtig, e.gesamt, beantwortet.length)} · ${dauerText(this.ablauf.aktivMs)}`),
    );
    t.appendChild(kopf);
    const unsicher = unsichereZeichen(beantwortet.map((a) => a.auswertung));
    if (unsicher.length) {
      const block = el("div", "morse-zeichen-block");
      block.append(el("p", "morse-feld-titel", "Noch unsicher — antippen zum Anhören"));
      const reihe = el("div", "morse-zeichenreihe");
      for (const x of unsicher.slice(0, 8)) reihe.appendChild(zeichenKnopf(x.zeichen, () => this.hoere(x.zeichen), { zusatz: `${x.falsch}× falsch` }));
      block.appendChild(reihe);
      t.appendChild(block);
    } else {
      t.appendChild(el("p", "morse-ergebnis-text", "Alles richtig — stark."));
    }
  }

  /** Ein unsicheres Zeichen zum Anhören (nach der Runde). */
  private hoere(z: string): void {
    if (this.zustand !== "laeuft") void this.u.spiele(z, true);
  }

  /** Nach dem Mikrofon den Ton wieder für die Rückmeldung wach halten (das Mikrofon hatte ihn aus der Pflicht genommen). */
  private wacheWieder(): void {
    if (!this.wachGeloest) return;
    this.wachGeloest = false;
    this.ablauf.halteWach(`weiter-nach-mikro reiter=${REITER}`);
  }

  /** „Zeigen“: der Buchstabe wird jetzt eingeblendet (bis zur Rückmeldung) — die Aufgabe zählt dann nicht. */
  private spicke(): void {
    if (this.zustand !== "laeuft" || !darfSpicken(this.anzeigeModus, this.phase, this.gespickt)) return;
    this.gespickt = true;
    this.u.spur(`gespickt reiter=${REITER} aufgabe=${this.index + 1}`);
    this.zeichneAnzeige();
  }

  /** Der große Buchstabe mit Morse-Bild (oder „?“), der Knopf „Zeigen“ und der Satz zum Schalter. */
  private zeichneAnzeige(): void {
    if (!this.zeichenAnzeige) return;
    const aktiv = this.zustand === "laeuft" || this.zustand === "pause";
    const z = this.aktuellesZeichen;
    const gezeigt = z !== "" && zeigtBuchstabe(this.anzeigeModus, this.phase, this.gespickt, aktiv);
    this.anzeigeWahl.setze(this.anzeigeModus);
    this.anzeigeText.textContent = AUGEN.anzeige.erklaerung[this.anzeigeModus];
    this.zeichenAnzeige.hidden = !aktiv || this.anzeigeModus === "aus";
    // Steht der Buchstabe in der FRAGE (hört, spricht) auf dem Bildschirm — durch „Zeigen“, „Immer“ von Anfang an oder ein Umschalten
    // mitten in der Frage —, gilt die Aufgabe als gespickt: nicht gebucht, nicht wiederholt. So lässt sich der Aufstieg nicht erschleichen.
    if (gezeigt && this.zustand === "laeuft" && (this.phase === "hoert" || this.phase === "spricht")) this.gespickt = true;
    this.zeichenAnzeige.dataset.gezeigt = String(gezeigt);
    this.zeichenAnzeige.textContent = "";
    // Screenreader: „Buchstabe M“ nur, wenn er gezeigt wird — verdeckt ist der Platzhalter stumm.
    if (gezeigt) {
      this.zeichenAnzeige.setAttribute("role", "img");
      const merk = merkwortElement(z, "morse-augen-merk");
      this.zeichenAnzeige.setAttribute("aria-label", merk ? `${AUGEN.anzeige.aria(z)}, ${merkwortAria(z)}` : AUGEN.anzeige.aria(z));
      this.zeichenAnzeige.removeAttribute("aria-hidden");
      this.zeichenAnzeige.append(el("span", "morse-augen-zeichen-name", z), musterBild(MORSE_TABELLE[z] ?? "", "l"), ...(merk ? [merk] : []));
    } else {
      this.zeichenAnzeige.removeAttribute("role");
      this.zeichenAnzeige.removeAttribute("aria-label");
      this.zeichenAnzeige.setAttribute("aria-hidden", "true");
      this.zeichenAnzeige.append(el("span", "morse-augen-zeichen-name", AUGEN.anzeige.platzhalter));
    }
    this.zeigenKnopf.hidden = !(aktiv && this.zustand === "laeuft" && darfSpicken(this.anzeigeModus, this.phase, this.gespickt));
  }

  /** Sofort still: keine Fortsetzung gilt mehr, keine Uhr, kein Ton, kein Mikrofon, keine Stimme. */
  private allesAus(): void {
    this.zeigeKandidaten(null);
    this.zuhoerer.abbrechen();
    try {
      this.stimmeLauf?.stopp();
    } catch {
      /* schon zu */
    }
    this.stimmeLauf = null;
    this.ablauf.allesAus();
  }
}
